import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compact, find, map, noop } from 'underscore';
import { AimAudienceAssociationView } from '@bingads-webui-campaign-legacy/audience-association/view/aim-component-view';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { getRoutePathById, withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { getAudiencePickerStyle } from './styles/audience-picker';

export const formatAudience = ({ audiences, i18n }) => {
  if (!audiences || !audiences.length) {
    return i18n.getString(_TL_('Targeting: No audience segments selected.'));
  }

  const targetedAudiences = audiences.filter(({ Status }) => Status === 'Active');
  const excludedAudiences = audiences.filter(({ Status }) => Status === 'Excluded');

  let targetedAudienceDetails = '';
  let excludedAudienceDetails = '';

  if (targetedAudiences.length === 1) {
    targetedAudienceDetails = i18n.getString(_TL_('Targeted: 1 audience'));
  } else if (targetedAudiences.length > 1) {
    targetedAudienceDetails = i18n.getString(_TL_('Targeted: {{count}} audiences'), { count: (targetedAudiences.length) });
  }

  if (excludedAudiences.length === 1) {
    excludedAudienceDetails = i18n.getString(_TL_('Excluded: 1 audience'));
  } else if (excludedAudiences.length > 1) {
    excludedAudienceDetails = i18n.getString(_TL_('Excluded: {{count}} audiences'), { count: (excludedAudiences.length) });
  }

  return `${targetedAudienceDetails}${targetedAudienceDetails && excludedAudienceDetails && '. '}${excludedAudienceDetails}`;
};

const audiencesLibraryId = 'audiences-library';
const tagsManagementId = 'uet-tag';

export const navigate = (siteMap, scopeService, routeId, state) => {
  const path = getRoutePathById(siteMap, routeId);

  scopeService.history.push({
    pathname: path,
    search: scopeService.history.location.search,
    state,
  });
};

const UnstyledAudiencePicker = (props) => {
  const {
    classes,
    validationProps,
    updateValidation,
    perfMarker,
    campaignSubType,
    campaignSettings,
    campaignBiddingStrategy,
    supportExclusion,
    siteMap,
    isAudienceCreationWorkflow,
    isIMARedesignEnabled,
    hideCustomSegmentsOption,
  } = props;

  const { data: { audiences } } = validationProps;

  const activity = useContext(ActivityContext);

  const [audienceAssociationRef, setAudienceAssociationRef] = useState();

  const { scope } = useContext(ScopeContext);

  const audienceAssociationView = useMemo(() => {
    const associationView = new AimAudienceAssociationView({
      isForVNext: true,
      perfMarker,
      campaignSubType,
      hideExclusion: !supportExclusion,
      hideAssociationHeader: true,
      biddingScheme: campaignBiddingStrategy,
      campaignSettings,
      isAudienceCreationWorkflow,
      deps: {
        router: {
          navigateToAudiences: data => navigate(siteMap, scope, audiencesLibraryId, data),
          navigateToTags: () => navigate(siteMap, scope, tagsManagementId),
        },
      },
      isIMARedesignEnabled,
      hideCustomSegmentsOption,
    });

    associationView.canExpandAssociation = true;
    associationView.render();
    associationView.expandAssociation();
    if (supportExclusion) {
      associationView.expandExclusion();
    }

    return associationView;
  }, [isAudienceCreationWorkflow, campaignBiddingStrategy, campaignSettings, campaignSubType, perfMarker, scope, siteMap, supportExclusion, isIMARedesignEnabled]);

  const onAudienceListChanged = useCallback(() => {
    const associationList = audienceAssociationView.getAudienceCriterion();

    updateValidation([], { audiences: [...associationList] });
    if (activity) {
      activity.signal(`onAudienceListChange - ${JSON.stringify({ audiences: associationList })}`);
    }
  }, [audienceAssociationView, updateValidation, activity]);

  useEffect(() => {
    if (audienceAssociationRef && audienceAssociationRef.childElementCount === 0 && audienceAssociationView) {
      audienceAssociationRef.innerHTML = '';
      audienceAssociationRef.append(audienceAssociationView.el);
    }
  }, [audienceAssociationRef, audienceAssociationView]);

  useEffect(() => {
    if (audienceAssociationView) {
      audienceAssociationView
        .ensureLoadedAsync()
        .then(() => {
          const associationList = compact(map(audiences, ({ Criterion, Status }) => find(audienceAssociationView.doubleListBoxViewModel.allAudiences, audience =>
            audience.Id === Criterion.AudienceId && Status === 'Active')));
          const exclusionList = compact(map(audiences, ({ Criterion, Status }) => find(audienceAssociationView.doubleListBoxViewModel.allAudiences, audience => audience.Id === Criterion.AudienceId && Status === 'Excluded')));

          audienceAssociationView.off('selectionChanged', onAudienceListChanged);
          audienceAssociationView.setLists({
            associationList,
            exclusionList,
          });

          audienceAssociationView.on('selectionChanged', onAudienceListChanged);
        });

      return () => {
        audienceAssociationView.off('selectionChanged', onAudienceListChanged);
      };
    }

    return noop;
  }, [audiences]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classNames('audience-picker', classes.pickerContainer)} >
      <div className={classNames('audience-association-view')} ref={newRef => setAudienceAssociationRef(newRef)} />
    </div>
  );
};

UnstyledAudiencePicker.propTypes = {
  classes: PropTypes.shape({
    pickerContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isAudienceCreationWorkflow: PropTypes.bool.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      audiences: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  campaignSubType: PropTypes.string,
  supportExclusion: PropTypes.bool,
  campaignSettings: PropTypes.shape({}).isRequired,
  campaignBiddingStrategy: PropTypes.shape({}).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  isIMARedesignEnabled: PropTypes.bool,
  hideCustomSegmentsOption: PropTypes.bool,
};

UnstyledAudiencePicker.defaultProps = {
  campaignSubType: null,
  supportExclusion: false,
  isIMARedesignEnabled: false,
  hideCustomSegmentsOption: false,
};

export const ValidatedAudiencePicker = withDefaultStyles(withSiteMap(withCustomValidation(UnstyledAudiencePicker)), getAudiencePickerStyle);
